import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Circle, Hourglass, AlertOctagon, Loader2, RefreshCw, ChevronDown } from "lucide-react";
import { fetchVerificationProgress } from "@lawyer/services/lawyerApi";
import { LAWYER_BASE } from "@lawyer/constants/route";

// Where a lawyer can act on each canonical requirement. Presentation only —
// statuses and percentages come exclusively from the backend evaluator.
const ACTION_ROUTE = {
  aadhaar: `${LAWYER_BASE}/kyc`,
  address_proof: `${LAWYER_BASE}/kyc`,
  photo: `${LAWYER_BASE}/kyc`,
  degree: `${LAWYER_BASE}/kyc`,
  bci_cert: `${LAWYER_BASE}/bar-council`,
  pan: `${LAWYER_BASE}/profile#payout`,
  payout_instrument: `${LAWYER_BASE}/profile#payout`,
  lawyer_level: `${LAWYER_BASE}/pricing`,
};

const REQUIREMENT_ORDER = [
  "application_approved",
  "aadhaar",
  "pan",
  "bci_cert",
  "degree",
  "photo",
  "address_proof",
  "payout_instrument",
  "no_duplicates",
  "lawyer_level",
];

const STATUS_UI = {
  met: { Icon: CheckCircle2, icon: "text-emerald-600", pill: null },
  pending: { Icon: Hourglass, icon: "text-amber-600", pill: ["Under review", "bg-amber-100 text-amber-800"] },
  rejected: { Icon: AlertOctagon, icon: "text-rose-600", pill: ["Action needed", "bg-rose-600 text-white"] },
  todo: { Icon: Circle, icon: "text-slate-300", pill: null },
};

// `initialData`: the same matrix, when the parent already has it (dashboard payload) — skips the initial fetch.
export default function VerificationProgressCard({
  refreshKey = 0,
  initialData = null,
  requirementTargets = null,
  onRequirementNavigate,
}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const mountKey = useRef(refreshKey);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchVerificationProgress()); }
    catch (err) { console.warn("[verification-progress] load failed", err?.response?.status || err?.message || err); setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    // Parent-supplied matrix covers the initial render; only refetch when the parent asks (refreshKey change).
    if (initialData && refreshKey === mountKey.current) return;
    load();
  }, [load, refreshKey, initialData]);
  useEffect(() => { if (initialData) setData(initialData); }, [initialData]);
  useEffect(() => {
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 min-h-[200px] grid place-items-center" data-testid="verification-progress-loading">
        <Loader2 size={20} className="animate-spin text-slate-300" />
      </div>
    );
  }
  if (!data) { // request failed or returned an empty payload
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="verification-progress-error">
        <p className="font-bold text-[#0A2540]">Verification Progress</p>
        <p className="text-sm text-slate-500 mt-1">We couldn&rsquo;t load your verification progress right now. Your data is safe — please try again.</p>
        <button type="button" onClick={load} data-testid="verification-progress-retry"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#0A2540] underline underline-offset-4">
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  const { percent, met, total, all_met, requirements } = data;
  const orderedRequirements = [...requirements].sort(
    (a, b) => REQUIREMENT_ORDER.indexOf(a.key) - REQUIREMENT_ORDER.indexOf(b.key),
  );
  if (all_met) {
    return (
      <div className="rounded-2xl overflow-hidden border border-emerald-200 bg-white shadow-sm" data-testid="verification-progress-card" data-complete="1">
        <div className="px-5 sm:px-6 py-5 bg-emerald-50/60">
          <div className="flex flex-wrap items-start gap-3 sm:flex-nowrap">
            <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0"><CheckCircle2 size={20} /></div>
            <div className="min-w-0 flex-1">
              <p className="text-lg font-extrabold text-emerald-900" style={{ fontFamily: "Poppins" }} data-testid="verification-complete-title">Verification complete</p>
              <p className="text-sm text-emerald-800" data-testid="verification-count">{met} of {total} requirements approved</p>
              <p className="text-xs text-emerald-700/80 mt-0.5" data-testid="verification-verified-by">Verified by Advoset Admin</p>
            </div>
            <p className="w-full text-right text-xl font-extrabold text-emerald-700 leading-none sm:w-auto sm:shrink-0" style={{ fontFamily: "Poppins" }} data-testid="verification-percent">{percent}%</p>
          </div>
          <div className="mt-3 box-border h-2 w-full rounded-full bg-emerald-100 overflow-hidden" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} data-testid="verification-progressbar">
            <div className="h-full rounded-full bg-gradient-to-r from-[#0A2540] via-[#12355B] to-[#D4AF37]" style={{ width: `${percent}%` }} />
          </div>
        </div>
        <details className="group">
          <summary className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-sm font-bold text-[#0A2540]" data-testid="verification-details-toggle">
            Verification details
            <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180" />
          </summary>
          <ol className="divide-y divide-slate-100 border-t border-slate-100" data-testid="verification-requirements">
            {orderedRequirements.map((r) => <RequirementRow key={r.key} req={r} requirementTargets={requirementTargets} onRequirementNavigate={onRequirementNavigate} />)}
          </ol>
        </details>
      </div>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm" data-testid="verification-progress-card">
      <div className="px-5 sm:px-6 pt-5 sm:pt-6">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 sm:flex-nowrap">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#0A2540] text-[#D4AF37] grid place-items-center shrink-0"><ShieldCheck size={18} /></div>
            <div className="min-w-0 flex-1">
              <p className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Verification Progress</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {`Complete all ${total} requirements to become fully verified. This is the same progress Advoset Admin sees.`}
              </p>
            </div>
          </div>
          <div className="w-full min-w-0 text-right sm:w-auto sm:shrink-0">
            <p className="text-2xl sm:text-3xl font-extrabold text-[#0A2540] leading-none" style={{ fontFamily: "Poppins" }} data-testid="verification-percent">{percent}%</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1" data-testid="verification-count">{met} of {total} requirements completed</p>
          </div>
        </div>
        <div className="mt-4 box-border h-2.5 w-full rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} data-testid="verification-progressbar">
          <div className="h-full rounded-full bg-gradient-to-r from-[#0A2540] via-[#12355B] to-[#D4AF37] transition-all duration-700 ease-out" style={{ width: `${percent}%` }} />
        </div>
      </div>
      <ol className="mt-4 divide-y divide-slate-100" data-testid="verification-requirements">
        {orderedRequirements.map((r) => <RequirementRow key={r.key} req={r} requirementTargets={requirementTargets} onRequirementNavigate={onRequirementNavigate} />)}
      </ol>
    </div>
  );
}

function RequirementRow({ req, requirementTargets, onRequirementNavigate }) {
  const ui = STATUS_UI[req.status] || STATUS_UI.todo;
  const href = req.status !== "met" ? ACTION_ROUTE[req.key] : null;
  const targetId = requirementTargets?.[req.key];
  const canScroll = !!(onRequirementNavigate && targetId);
  const inner = (
    <>
      <span className="mt-0.5 shrink-0" aria-hidden><ui.Icon size={20} className={ui.icon} /></span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 flex-wrap">
          <span className={`font-bold text-sm ${req.status === "met" ? "text-slate-600" : req.status === "rejected" ? "text-rose-800" : "text-[#0A2540]"}`}>{req.label}</span>
          {ui.pill && <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ui.pill[1]}`}>{ui.pill[0]}</span>}
        </span>
        {req.detail && <span className={`block text-xs mt-0.5 ${req.status === "rejected" ? "text-rose-700" : "text-slate-500"}`}>{req.detail}</span>}
      </span>
    </>
  );
  const cls = `flex items-start gap-3 px-5 sm:px-6 py-3.5 ${req.status === "rejected" ? "bg-rose-50/60" : ""} ${href || canScroll ? "hover:bg-slate-50" : ""}`;
  return (
    <li data-testid={`verification-req-${req.key}`} data-status={req.status}>
      {canScroll ? (
        <button type="button" onClick={() => onRequirementNavigate(targetId)}
          className={`${cls} w-full text-left cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#D4AF37]`}
          data-testid={`verification-req-${req.key}-scroll`}>
          {inner}
        </button>
      ) : !onRequirementNavigate && href ? (
        <Link to={href} className={`${cls} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#D4AF37]`}>
          {inner}
        </Link>
      ) : (
        <div className={cls}>{inner}</div>
      )}
    </li>
  );
}
